import React, { useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { WebNotificationType } from 'Shared/constants/types';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { NotificationsContext } from '../../../../NotificationsProviderContext';
import { WpButton } from 'Wpds/components/button';
import { WpLink } from 'Wpds/components/link';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { Tr } from 'Shared/components/i18n/Tr';

type Props = {
  onCreateActionItemClick: Function;
  notification: {
    users: Array<{
      id: number;
      firstName: string;
      email: string;
    }>;
    data: {
      type: string;
      link: string;
    };
  };
};

function CreateActionItemLinkContent({ onCreateActionItemClick, notification }: Props) {
  const { tr } = useTranslation();

  const { handleSetIsOpen } = useContext(NotificationsContext);
  const { feedbackActionItem } = useFlags();
  const { id: currentUserId } = useCurrentUser();
  const teammate = notification.users && notification.users.find(({ id }) => id !== currentUserId);

  const handleGiveFeedbackReminderLinkClick = () => {
    handleSetIsOpen(false);
    onCreateActionItemClick({ teammate });
  };

  const handleDraftFeedbackReminderLinkClick = () => {
    handleSetIsOpen(false);
    const title = tr('perform.notifications.finish_giving_feedback_title', teammate?.firstName || teammate?.email);
    onCreateActionItemClick({ teammate, title });
  };

  switch (notification?.data?.type) {
    case WebNotificationType.giveFeedbackReminder:
      return feedbackActionItem ? (
        <span>
          &nbsp;
          <Tr
            trKey="perform.notifications.give_feedback_reminder_full_message"
            components={{
              link: p => (
                <WpLink href={notification.data?.link} onClick={() => handleSetIsOpen(false)}>
                  {p.children}
                </WpLink>
              ),
              button: p => (
                <WpButton variant="link" onClick={handleGiveFeedbackReminderLinkClick}>
                  {p.children}
                </WpButton>
              ),
            }}
          />
        </span>
      ) : null;
    case WebNotificationType.draftFeedback:
      return (
        <span>
          &nbsp;
          <Tr
            trKey="perform.notifications.draft_feedback_full_message"
            components={{
              link: p => (
                <WpLink href={notification.data?.link} onClick={() => handleSetIsOpen(false)}>
                  {p.children}
                </WpLink>
              ),
              button: p => (
                <WpButton variant="link" onClick={handleDraftFeedbackReminderLinkClick}>
                  {p.children}
                </WpButton>
              ),
            }}
          />
        </span>
      );
    case WebNotificationType.feedbackRequested:
    case WebNotificationType.feedbackRequestedReminder:
      return (
        <span>
          &nbsp;
          <Tr
            trKey="perform.notifications.feedback_requested_full_message"
            components={{
              link: p => (
                <WpLink href={notification.data?.link} onClick={() => handleSetIsOpen(false)}>
                  {p.children}
                </WpLink>
              ),
              button: p => (
                <WpButton variant="link" onClick={handleGiveFeedbackReminderLinkClick}>
                  {p.children}
                </WpButton>
              ),
            }}
          />
        </span>
      );
    default:
      return null;
  }
}

export default CreateActionItemLinkContent;
